import React from 'react';
import { Button } from '../../components/ui/button';
import { Settings } from 'lucide-react';
import LegalLayout from './LegalLayout';
import { useCookieConsent } from '../../context/CookieConsentContext';

const Cookies = () => {
  const { openPreferences } = useCookieConsent();

  return (
    <LegalLayout
      testId="legal-cookies-page"
      title="Cookie Notice"
      lastUpdated="February 2026"
      intro="This Cookie Notice explains what cookies are, how Daye Farm Ltd uses them and how you can manage your preferences. It should be read together with our Privacy Policy."
    >
      <h2>1. What Are Cookies?</h2>
      <p>
        Cookies are small text files stored on your device when you visit a website. They allow the
        site to recognise your device, remember preferences and improve your experience. We also
        use similar technologies such as <em>localStorage</em>, pixels and SDKs, all referred to as
        "cookies" in this Notice.
      </p>

      <h2>2. Categories of Cookies We Use</h2>
      <ul>
        <li>
          <strong>Strictly Necessary (Essential):</strong> required for the site to function. They
          power secure authentication (httpOnly session cookies), shopping cart, checkout and
          fraud-prevention. These cannot be turned off.
        </li>
        <li>
          <strong>Functional:</strong> remember preferences such as language, region and live chat
          continuity so the experience feels personal.
        </li>
        <li>
          <strong>Analytics &amp; Performance:</strong> help us understand how visitors use Daye
          Farm so we can improve content and performance. Data is aggregated and never sold.
        </li>
        <li>
          <strong>Personalization:</strong> tailor product recommendations and content across the
          Farmers Market and Investment portal based on your interests.
        </li>
        <li>
          <strong>Advertising:</strong> allow us and trusted partners to measure ad performance and
          deliver more relevant marketing for our farm products.
        </li>
      </ul>

      <h2>3. First-Party vs Third-Party Cookies</h2>
      <p>
        First-party cookies are set by <strong>dayefarmltd.com</strong>. Third-party cookies are
        set by partners such as Google (sign-in, analytics), payment processors and social
        platforms. Each provider is responsible for their own data practices and we recommend
        reviewing their notices.
      </p>

      <h2>4. How Long Cookies Last</h2>
      <ul>
        <li>
          <strong>Session cookies</strong> are deleted when you close your browser (e.g. login
          session).
        </li>
        <li>
          <strong>Persistent cookies</strong> remain on your device until they expire or are
          deleted (e.g. saved preferences). Most of our persistent cookies expire within 13 months.
        </li>
      </ul>

      <h2>5. Managing Your Choices</h2>
      <p>
        When you first visit Daye Farm you will see a cookie banner. You can <strong>Accept All</strong>,{' '}
        <strong>Reject Non-Essential</strong> or open <strong>Customize</strong> to toggle each
        category individually. You can reopen the preference panel at any time using the button
        below or the "Manage Cookies" link in the footer.
      </p>

      <Button
        onClick={openPreferences}
        className="bg-green-600 hover:bg-green-700 text-white not-prose"
        data-testid="legal-manage-cookies-btn"
      >
        <Settings className="h-4 w-4 mr-2" /> Manage Cookie Preferences
      </Button>

      <h2 className="mt-10">6. Browser-Level Controls</h2>
      <p>
        Most browsers let you view, delete and block cookies through their settings. Please note
        that disabling essential cookies will prevent core features such as login and checkout from
        working. Useful links:
      </p>
      <ul>
        <li>
          <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noreferrer">
            Google Chrome
          </a>
        </li>
        <li>
          <a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noreferrer">
            Mozilla Firefox
          </a>
        </li>
        <li>
          <a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" target="_blank" rel="noreferrer">
            Apple Safari
          </a>
        </li>
        <li>
          <a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noreferrer">
            Microsoft Edge
          </a>
        </li>
      </ul>

      <h2>7. Do Not Track Signals</h2>
      <p>
        Some browsers transmit a "Do Not Track" (DNT) signal. There is currently no industry
        consensus on DNT, so Daye Farm relies primarily on your choices in our cookie banner and
        preference centre.
      </p>

      <h2>8. Updates</h2>
      <p>
        We may update this Cookie Notice as our practices evolve or as regulations change. Material
        updates will be highlighted on the site.
      </p>

      <h2>9. Contact</h2>
      <p>
        Questions? Email{' '}
        <a href="mailto:privacy@dayefarmltd.com">privacy@dayefarmltd.com</a> or write to Daye Farm
        Ltd, 110 Ankpa Ayingba Road, Kogi, Nigeria.
      </p>
    </LegalLayout>
  );
};

export default Cookies;
